import { Link } from 'react-router-dom';
import { OrderStatus, formatMoney } from '@lombok/shared';
import type { Customer, OrderView } from '@lombok/shared';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { orderLabel } from '@/lib/names';
import { isPriced } from '@/lib/orderView';
import { useSessionProfile } from '@/auth/useSessionProfile';
import { useCustomer, useCustomers, useInventory, useOrders } from '@/data/hooks';
import { useRepeatOrder } from '@/features/orders/useRepeatOrder';
import { canApproveOrders } from '@/features/orders/approval';
import { subaccountTotals, topBalances } from '@/features/distributors/portal';
import { useCanCreateOrders } from '@/auth/useCanCreateOrders';

/**
 * Role-aware portal home screens (customer / sales / distributor / warehouse).
 * Each pulls real data via the typed hooks + the session profile and follows the
 * b2b-commerce-domain priorities (customer = balance/reorder/payment proof; sales
 * = assigned customers + quick order + approval queue; distributor = wholesale
 * catalog + orders). Money is always via shared helpers (USD).
 */

/** A tappable shortcut tile (large target, mobile-first). */
function Shortcut({ to, title, desc }: { to: string; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 rounded-md border border-border p-4 transition-[background-color,border-color,transform] duration-150 ease-out hover:border-border-strong hover:bg-surface-muted active:scale-[0.99] motion-reduce:active:scale-100"
    >
      <span className="text-sm font-semibold text-text-primary">{title}</span>
      <span className="text-xs text-text-muted">{desc}</span>
    </Link>
  );
}

/**
 * "Reordenar": loads the customer's latest real order (not a draft, cancelled or rejected one) into a new
 * order, where they review current prices and availability. Any other order can be repeated from its detail.
 */
function ReorderShortcut({ orders }: { orders: OrderView[] }) {
  const { repeat, pendingId, error } = useRepeatOrder();
  const skipped: string[] = [OrderStatus.DRAFT, OrderStatus.CANCELLED, OrderStatus.REJECTED];
  const last = orders
    .filter((o) => !skipped.includes(o.status))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  return (
    <button
      type="button"
      disabled={!last || pendingId !== null}
      onClick={() => last && void repeat(last.orderId)}
      className="flex flex-col gap-1 rounded-md border border-border p-4 text-left transition-[background-color,border-color,transform] duration-150 ease-out hover:border-border-strong hover:bg-surface-muted active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 motion-reduce:active:scale-100"
    >
      <span className="text-sm font-semibold text-text-primary">Reordenar</span>
      <span className="text-xs text-text-muted">
        {pendingId ? 'Cargando…' : last ? `Repetir ${orderLabel(last)}` : 'Aún no tienes pedidos'}
      </span>
      {error ? (
        <span role="alert" className="text-xs text-danger">
          {error}
        </span>
      ) : null}
    </button>
  );
}

/** A single labeled KPI value (money or count). */
function Kpi({ label, value, tone }: { label: string; value: string; tone?: 'default' | 'success' }) {
  return (
    <Card>
      <CardContent className="pt-5">
        <p className="text-sm text-text-muted">{label}</p>
        <p
          className={`mt-1 text-2xl font-semibold tracking-tight ${
            tone === 'success' ? 'text-success' : 'text-text-primary'
          }`}
        >
          {value}
        </p>
      </CardContent>
    </Card>
  );
}

/** Compact recent-orders list shared by the portals (read-only preview). */
function RecentOrders({
  orders,
  detailBase,
  emptyLabel = 'Aún no tienes pedidos.',
}: {
  orders: OrderView[];
  detailBase: string;
  emptyLabel?: string;
}) {
  if (orders.length === 0) {
    return <p className="px-1 py-2 text-sm text-text-muted">{emptyLabel}</p>;
  }
  return (
    <ul className="flex flex-col">
      {orders.slice(0, 5).map((o) => (
        <li key={o.orderId} className="border-b border-border last:border-0">
          <Link
            to={`${detailBase}/${o.orderId}`}
            className="flex items-center justify-between gap-3 py-3 text-sm active:bg-surface-muted"
          >
            <span className="min-w-0 truncate font-mono text-brand-primary">{orderLabel(o)}</span>
            <span className="flex shrink-0 items-center gap-3">
              {isPriced(o) ? (
                <span className="font-medium text-text-primary">{formatMoney(o.totalMinorUsd)}</span>
              ) : null}
              <StatusPill kind="order" value={o.status} />
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

function greeting(fullName?: string, fallback = 'Bienvenido.') {
  return fullName ? `Hola, ${fullName}.` : fallback;
}

/* -------------------------------- Customer -------------------------------- */

export function CustomerHome() {
  const { profile } = useSessionProfile();
  const customerId = profile?.customerId;
  const customerQuery = useCustomer(customerId);
  const ordersQuery = useOrders(customerId ? { customerId, limit: 5 } : undefined, Boolean(customerId));

  const c: Customer | undefined = customerQuery.data;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Inicio" description={greeting(profile?.fullName, 'Portal de cliente.')} />

      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi label="Saldo pendiente" value={c ? formatMoney(c.openBalanceMinorUsd) : '—'} />
        <Kpi
          label="Crédito disponible"
          value={c ? formatMoney(c.availableCreditMinorUsd) : '—'}
          tone="success"
        />
        <Kpi label="Límite de crédito" value={c ? formatMoney(c.creditLimitMinorUsd) : '—'} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accesos rápidos</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 pt-0 sm:grid-cols-2 lg:grid-cols-4">
          <Shortcut to="/app/customer/catalogo" title="Catálogo" desc="Ver disponibilidad" />
          <ReorderShortcut orders={ordersQuery.data?.items ?? []} />
          <Shortcut
            to="/app/customer/pagos/subir"
            title="Subir comprobante de pago"
            desc="Pago móvil, Zelle, transferencia"
          />
          <Shortcut to="/app/customer/facturas" title="Facturas" desc="Estado de cuenta" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Mis pedidos recientes</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {!customerId ? (
            <p className="px-1 py-2 text-sm text-text-muted">
              Tu usuario aún no está vinculado a una cuenta de cliente.
            </p>
          ) : ordersQuery.isLoading ? (
            <p className="px-1 py-2 text-sm text-text-muted">Cargando pedidos…</p>
          ) : (
            <RecentOrders orders={ordersQuery.data?.items ?? []} detailBase="/app/customer/pedidos" />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* --------------------------------- Sales ---------------------------------- */

export function SalesHome() {
  const { profile } = useSessionProfile();
  const customersQuery = useCustomers({ limit: 100 });
  // Only roles that approve get the queue; a rep sees their clients' recent orders instead.
  const canApprove = canApproveOrders(profile?.role);
  const queueQuery = useOrders({ status: OrderStatus.PENDING_APPROVAL, limit: 5 }, canApprove);
  const recentQuery = useOrders({ limit: 5 }, !canApprove);

  const assigned = (customersQuery.data?.items ?? []).filter(
    (c) => !profile?.salesRepId || c.assignedSalesRepId === profile.salesRepId,
  );
  // Total open balance across the rep's book (real receivables, not a goal placeholder).
  const carteraMinor = assigned.reduce((sum, c) => sum + c.openBalanceMinorUsd, 0);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Inicio"
        description={greeting(profile?.fullName, 'Portal de ventas.')}
        actions={
          <Link to="/app/sales/crear-pedido">
            <Button size="sm">Crear pedido</Button>
          </Link>
        }
      />

      <div className={`grid gap-4 ${canApprove ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
        <Kpi label="Mis clientes" value={customersQuery.isLoading ? '…' : String(assigned.length)} />
        {canApprove ? (
          <Kpi
            label="Pedidos por aprobar"
            value={queueQuery.isLoading ? '…' : String(queueQuery.data?.items.length ?? 0)}
          />
        ) : null}
        <Kpi
          label="Saldo de cartera"
          value={customersQuery.isLoading ? '…' : formatMoney(carteraMinor)}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Mis clientes asignados</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {customersQuery.isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando clientes…</p>
            ) : assigned.length === 0 ? (
              <p className="px-1 py-2 text-sm text-text-muted">No tienes clientes asignados.</p>
            ) : (
              <ul className="flex flex-col">
                {assigned.slice(0, 6).map((c) => (
                  <li key={c.customerId} className="border-b border-border last:border-0">
                    <Link
                      to={`/app/sales/clientes/${c.customerId}`}
                      className="flex items-center justify-between gap-3 py-3 text-sm active:bg-surface-muted"
                    >
                      <span className="font-medium text-text-primary">{c.name}</span>
                      <span className="text-text-muted">{formatMoney(c.openBalanceMinorUsd)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-3">
              <Link to="/app/sales/clientes" className="text-sm text-brand-primary hover:underline">
                Ver todos los clientes
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{canApprove ? 'Cola de pedidos por aprobar' : 'Pedidos recientes'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {(canApprove ? queueQuery : recentQuery).isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando…</p>
            ) : canApprove ? (
              <RecentOrders
                orders={queueQuery.data?.items ?? []}
                detailBase="/app/sales/pedidos"
                emptyLabel="No hay pedidos pendientes de aprobación."
              />
            ) : (
              <RecentOrders
                orders={recentQuery.data?.items ?? []}
                detailBase="/app/sales/pedidos"
                emptyLabel="Aún no hay pedidos de tus clientes."
              />
            )}
            <div className="pt-3">
              <Link to="/app/sales/pedidos" className="text-sm text-brand-primary hover:underline">
                Ver todos los pedidos
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ------------------------------ Distributor ------------------------------- */

export function DistributorHome() {
  const { profile } = useSessionProfile();
  // Distributor scope (orders, sub-accounts) is enforced server-side; no client filter required.
  const ordersQuery = useOrders({ limit: 5 });
  const subaccountsQuery = useCustomers({ limit: 100 });
  const canCreateOrders = useCanCreateOrders();

  const subaccounts = subaccountsQuery.data?.items ?? [];
  const totals = subaccountTotals(subaccounts);
  const owing = topBalances(subaccounts);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Panel"
        description={greeting(profile?.fullName, 'Portal de distribuidor.')}
        actions={
          canCreateOrders ? (
            <Link to="/app/distributor/crear-pedido">
              <Button size="sm">Crear pedido</Button>
            </Link>
          ) : undefined
        }
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Kpi label="Subclientes" value={subaccountsQuery.isLoading ? '…' : String(totals.count)} />
        <Kpi
          label="Saldo pendiente de subclientes"
          value={subaccountsQuery.isLoading ? '…' : formatMoney(totals.openBalanceMinorUsd)}
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Shortcut to="/app/distributor/catalogo" title="Catálogo mayorista" desc="Precios de mayoreo" />
        <Shortcut to="/app/distributor/pedidos" title="Pedidos" desc="Pedidos de tus subclientes" />
        <Shortcut to="/app/distributor/subclientes" title="Subclientes" desc="Tus clientes y sus saldos" />
        <Shortcut to="/app/distributor/facturas" title="Facturas" desc="Facturas de tus subclientes" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Subclientes con saldo pendiente</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {subaccountsQuery.isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando subclientes…</p>
            ) : subaccountsQuery.isError ? (
              <p className="px-1 py-2 text-sm text-danger">No se pudieron cargar tus subclientes. Intenta de nuevo.</p>
            ) : owing.length === 0 ? (
              <p className="px-1 py-2 text-sm text-text-muted">
                {totals.count === 0 ? 'Aún no tienes subclientes.' : 'Ninguno de tus subclientes tiene saldo pendiente.'}
              </p>
            ) : (
              <ul className="flex flex-col">
                {owing.map((c) => (
                  <li key={c.customerId} className="border-b border-border last:border-0">
                    <Link
                      to={`/app/distributor/subclientes/${c.customerId}`}
                      className="flex items-center justify-between gap-3 py-3 text-sm active:bg-surface-muted"
                    >
                      <span className="font-medium text-text-primary">{c.name}</span>
                      <span className="text-text-muted">{formatMoney(c.openBalanceMinorUsd)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-3">
              <Link to="/app/distributor/subclientes" className="text-sm text-brand-primary hover:underline">
                Ver todos los subclientes
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Pedidos recientes</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {ordersQuery.isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando pedidos…</p>
            ) : ordersQuery.isError ? (
              <p className="px-1 py-2 text-sm text-danger">
                No se pudieron cargar tus pedidos. Intenta de nuevo.
              </p>
            ) : (
              <RecentOrders orders={ordersQuery.data?.items ?? []} detailBase="/app/distributor/pedidos" />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/* ------------------------------- Warehouse -------------------------------- */

export function WarehouseHome() {
  const { profile } = useSessionProfile();
  // Warehouse order scope is enforced server-side; we pass a concrete status.
  const toDispatchQuery = useOrders({ status: OrderStatus.APPROVED, limit: 50 });
  const toDeliverQuery = useOrders({ status: OrderStatus.DISPATCHED, limit: 50 });
  const lowStockQuery = useInventory({ lowStock: true });

  const toDispatch = toDispatchQuery.data?.items ?? [];
  const toDeliver = toDeliverQuery.data?.items ?? [];
  const lowStockCount = lowStockQuery.data?.items.length ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Inicio" description={greeting(profile?.fullName, 'Operación de almacén.')} />

      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi
          label="Por despachar"
          value={toDispatchQuery.isLoading ? '…' : String(toDispatch.length)}
        />
        <Kpi
          label="Por entregar"
          value={toDeliverQuery.isLoading ? '…' : String(toDeliver.length)}
        />
        <Kpi
          label="SKU bajo stock"
          value={lowStockQuery.isLoading ? '…' : String(lowStockCount)}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Accesos rápidos</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 pt-0 sm:grid-cols-2 lg:grid-cols-3">
          <Shortcut to="/app/warehouse/pedidos" title="Pedidos" desc="Preparar y despachar" />
          <Shortcut to="/app/warehouse/despachos" title="Despachos" desc="Confirmar entregas" />
          <Shortcut to="/app/warehouse/inventario" title="Inventario" desc="Existencias y bajo stock" />
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Por despachar</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {toDispatchQuery.isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando…</p>
            ) : toDispatchQuery.isError ? (
              <p className="px-1 py-2 text-sm text-danger">
                No se pudieron cargar los pedidos. Intenta de nuevo.
              </p>
            ) : (
              <RecentOrders
                orders={toDispatch}
                detailBase="/app/warehouse/pedidos"
                emptyLabel="No hay pedidos aprobados por despachar."
              />
            )}
            <div className="pt-3">
              <Link to="/app/warehouse/pedidos" className="text-sm text-brand-primary hover:underline">
                Ver cola de fulfillment
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Por entregar</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {toDeliverQuery.isLoading ? (
              <p className="px-1 py-2 text-sm text-text-muted">Cargando…</p>
            ) : toDeliverQuery.isError ? (
              <p className="px-1 py-2 text-sm text-danger">
                No se pudieron cargar los despachos. Intenta de nuevo.
              </p>
            ) : (
              <RecentOrders
                orders={toDeliver}
                detailBase="/app/warehouse/pedidos"
                emptyLabel="No hay pedidos despachados por entregar."
              />
            )}
            <div className="pt-3">
              <Link to="/app/warehouse/despachos" className="text-sm text-brand-primary hover:underline">
                Ver despachos
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
